import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Search, Wrench, UserX } from 'lucide-react';

function ProblemSection({ content }) {
  const problems = [
    {
      icon: UserX,
      title: 'Talent Acquisition Crisis',
      description: "Businesses struggle to find qualified professionals. Indeed and Craigslist don't understand the industry. Word-of-mouth is slow and unreliable.",
      stat: '68%',
      statLabel: 'of salon owners cite hiring as their #1 challenge',
      color: 'coral'
    },
    {
      icon: Wrench,
      title: 'Fragmented Tools',
      description: 'Professionals juggle 5+ disconnected tools: Instagram for marketing, Square for payments, Booksy for scheduling, LinkedIn for networking.',
      stat: '$30-80',
      statLabel: 'monthly wasted on fragmented solutions',
      color: 'gold'
    },
    {
      icon: Search,
      title: 'No Professional Identity',
      description: 'Beauty professionals lack a dedicated platform to build their career presence, showcase work, and connect with opportunities.',
      stat: 'Zero',
      statLabel: 'platforms combine networking + job marketplace',
      color: 'lavender'
    }
  ];

  const colorClasses = {
    coral: 'bg-accent-coral/10 text-accent-coral border-accent-coral/20',
    gold: 'bg-accent-gold/10 text-accent-gold border-accent-gold/20',
    lavender: 'bg-accent-lavender/10 text-accent-lavender border-accent-lavender/20'
  };

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-coral/10 border border-accent-coral/20 rounded-full mb-6">
            <AlertTriangle className="w-4 h-4 text-accent-coral" />
            <span className="text-sm text-accent-coral">The Problem</span>
          </div>
          <h2 className="section-title mb-4">
            A <span className="text-gradient">$75 Billion</span> Industry
            <br />Without Professional Infrastructure
          </h2>
          <p className="section-subtitle mx-auto">
            The beauty industry is one of the largest service sectors in America, yet it lacks the professional tools available to every other industry.
          </p>
        </motion.div>

        {/* Problem Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {problems.map((problem, index) => {
            const Icon = problem.icon;
            return (
              <motion.div
                key={problem.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.15 }}
                className="card hover-lift group"
              >
                <div className={`inline-flex p-3 rounded-xl ${colorClasses[problem.color]} mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>

                <h3 className="text-xl font-semibold mb-3 group-hover:text-kore-400 transition-colors">
                  {problem.title}
                </h3>

                <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                  {problem.description}
                </p>

                <div className="pt-4 border-t border-surface-border">
                  <div className="text-3xl font-bold text-gradient">{problem.stat}</div>
                  <div className="text-xs text-zinc-500 mt-1">{problem.statLabel}</div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom stat */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center gap-4 px-8 py-4 bg-surface-card border border-surface-border rounded-2xl">
            <div className="text-4xl font-bold text-accent-coral">$700+</div>
            <div className="text-left">
              <div className="text-sm font-medium text-white">Monthly spend per business</div>
              <div className="text-xs text-zinc-500">on disconnected software tools</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ProblemSection;
